import React from 'react';

/*
 * Renderers for the user manual's content blocks (see common/manual/index.js for the
 * block schema). Content lives as plain data so it stays readable and diffable; this
 * module is the only place that knows how a block becomes markup.
 *
 * Everything here styles itself with the app's ordinary utility classes — index.css
 * redefines those under `.dark`, so the manual follows the app's theme for free.
 */

// Lightweight inline markup, so content strings stay free of JSX:
//   **bold**            emphasis
//   `Save`              a UI control the reader will look for on screen
//   {{Disputes|#id}}    a link to another section of the manual
const INLINE_PATTERN = /(\*\*[^*]+\*\*|`[^`]+`|\{\{[^}]+\}\})/g;

export function Inline({ text }) {
  if (!text) return null;
  const parts = String(text).split(INLINE_PATTERN).filter(Boolean);

  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <strong key={i} className="font-semibold text-gray-900">
              {part.slice(2, -2)}
            </strong>
          );
        }

        if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <code
              key={i}
              className="whitespace-nowrap rounded border border-gray-200 bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-gray-800"
            >
              {part.slice(1, -1)}
            </code>
          );
        }

        if (part.startsWith('{{') && part.endsWith('}}')) {
          const [label, href] = part.slice(2, -2).split('|');
          return (
            <a key={i} href={href} className="text-cloudza-600 underline underline-offset-2 hover:text-cloudza-700">
              {label}
            </a>
          );
        }

        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
}

const NOTE_STYLES = {
  info:    { wrap: 'border-gray-300 bg-gray-50',      tag: 'text-gray-500' },
  tip:     { wrap: 'border-cloudza-400 bg-cloudza-50', tag: 'text-cloudza-700' },
  caution: { wrap: 'border-amber-400 bg-amber-50',     tag: 'text-amber-700' },
  danger:  { wrap: 'border-red-400 bg-red-50',         tag: 'text-red-700' },
};

// Status pills reuse the same colour language as the pages they describe, so a
// reader recognises the badge here as the one they saw on Jobs or a call.
const STATUS_STYLES = {
  ok:   'bg-green-50 text-green-700 border-green-200',
  warn: 'bg-amber-50 text-amber-800 border-amber-200',
  bad:  'bg-red-50 text-red-700 border-red-200',
  idle: 'bg-gray-100 text-gray-600 border-gray-300',
};

export function StatusPill({ tone = 'idle', children }) {
  return (
    <span className={`inline-block rounded border px-1.5 py-0.5 font-mono text-[11px] ${STATUS_STYLES[tone] || STATUS_STYLES.idle}`}>
      {children}
    </span>
  );
}

function Paragraph({ block }) {
  return (
    <p className="mb-4 text-[15px] leading-relaxed text-gray-700">
      <Inline text={block.text} />
    </p>
  );
}

function Heading({ block }) {
  return (
    <h3 className="mb-2 mt-8 text-lg font-bold text-gray-900">
      <Inline text={block.text} />
    </h3>
  );
}

function BulletList({ block }) {
  return (
    <ul className="mb-4 space-y-2">
      {block.items.map((item, i) => (
        <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed text-gray-700">
          <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-gray-400" aria-hidden="true" />
          <span>
            <Inline text={typeof item === 'string' ? item : item.text} />
            {typeof item !== 'string' && item.items && (
              <ul className="mt-2 space-y-1.5">
                {item.items.map((sub, j) => (
                  <li key={j} className="flex gap-2.5">
                    <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-gray-300" aria-hidden="true" />
                    <span><Inline text={sub} /></span>
                  </li>
                ))}
              </ul>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Steps({ block }) {
  return (
    <ol className="mb-5 space-y-3.5">
      {block.items.map((item, i) => (
        <li key={i} className="flex gap-3.5">
          <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-cloudza-600 font-mono text-[11px] text-white">
            {i + 1}
          </span>
          <div className="min-w-0 text-[15px] leading-relaxed text-gray-700">
            {item.title && (
              <span className="font-semibold text-gray-900">
                <Inline text={item.title} />{' '}
              </span>
            )}
            <Inline text={item.text} />
            {item.items && (
              <ul className="mt-2 space-y-1.5">
                {item.items.map((sub, j) => (
                  <li key={j} className="flex gap-2.5">
                    <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-gray-400" aria-hidden="true" />
                    <span><Inline text={sub} /></span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function Note({ block }) {
  const style = NOTE_STYLES[block.variant] || NOTE_STYLES.info;
  return (
    <div className={`mb-5 rounded-r border-l-4 px-4 py-3 ${style.wrap}`}>
      {block.tag && (
        <div className={`mb-1 font-mono text-[11px] uppercase tracking-wider ${style.tag}`}>
          {block.tag}
        </div>
      )}
      <p className="text-[15px] leading-relaxed text-gray-700">
        <Inline text={block.text} />
      </p>
    </div>
  );
}

function Table({ block }) {
  return (
    <div className="mb-5">
      {block.caption && (
        <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-gray-500">
          {block.caption}
        </div>
      )}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              {block.head.map((cell, i) => (
                <th key={i} className="px-3 py-2.5 font-semibold text-gray-900">
                  <Inline text={cell} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => (
              <tr key={i} className="border-b border-gray-100 last:border-b-0">
                {row.map((cell, j) => (
                  <td key={j} className="px-3 py-2.5 align-top text-gray-700">
                    <Inline text={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {block.footnote && (
        <p className="mt-2 text-xs text-gray-500">
          <Inline text={block.footnote} />
        </p>
      )}
    </div>
  );
}

function Definitions({ block }) {
  return (
    <dl className="mb-5 space-y-3.5">
      {block.items.map((item, i) => (
        <div key={i}>
          <dt className="text-[15px] font-semibold text-gray-900">
            <Inline text={item.term} />
          </dt>
          <dd className="mt-0.5 text-[15px] leading-relaxed text-gray-700">
            <Inline text={item.text} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

const BLOCKS = {
  p: Paragraph,
  h3: Heading,
  ul: BulletList,
  steps: Steps,
  note: Note,
  table: Table,
  defs: Definitions,
};

export default function ManualBlock({ block }) {
  const Component = BLOCKS[block.type];
  if (!Component) return null;
  return <Component block={block} />;
}
